import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { 
  Home, Users, Package, CreditCard, BarChart3, LifeBuoy, Settings,
  Building2, Shield, UserPlus, ShieldCheck, LayoutDashboard, Users2, Settings2,
  FileText, Truck, FileCheck, Receipt, Activity, Search
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: any;
  path: string;
  category: 'admin' | 'enterprise' | 'recent';
  keywords?: string[];
}

export const AdminCommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [recentPages, setRecentPages] = useState<string[]>([]);
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin, canManageSysco, canViewReports, canManageBilling } = useAdminAccess();

  // Keyboard shortcuts: Cmd+K / Ctrl+K (open palette), Cmd+H / Ctrl+H (back to app)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
      if (e.key === 'h' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        navigate('/app/dashboard');
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [navigate]);

  // Load recent pages from localStorage
  useEffect(() => {
    const recent = localStorage.getItem('admin-recent-pages');
    if (recent) {
      setRecentPages(JSON.parse(recent));
    }
  }, []);

  const addToRecent = useCallback((path: string) => {
    setRecentPages((prev) => {
      const updated = [path, ...prev.filter(p => p !== path)].slice(0, 5);
      localStorage.setItem('admin-recent-pages', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleSelect = useCallback((path: string) => {
    setOpen(false);
    addToRecent(path);
    navigate(path);
  }, [navigate, addToRecent]);

  if (!isAdmin) {
    return null;
  }

  // Admin menu items
  const adminItems = [
    { 
      id: 'back-to-app', 
      label: 'Back to App Dashboard', 
      description: 'Return to CampChef Pro',
      icon: Home, 
      path: '/app/dashboard', 
      category: 'admin',
      keywords: ['home', 'dashboard', 'app', 'return', 'exit', 'leave']
    },
    { 
      id: 'admin-dashboard', 
      label: 'Dashboard', 
      description: 'Admin home and overview',
      icon: Home, 
      path: '/admin/dashboard', 
      category: 'admin',
      keywords: ['home', 'overview', 'admin']
    },
    { 
      id: 'admin-users', 
      label: 'Users', 
      description: 'Manage users and roles',
      icon: Users, 
      path: '/admin/users', 
      category: 'admin',
      keywords: ['users', 'roles', 'permissions', 'invite']
    },
    { 
      id: 'admin-billing', 
      label: 'Billing', 
      description: 'Invoices and subscriptions',
      icon: CreditCard, 
      path: '/admin/billing', 
      category: 'admin',
      keywords: ['billing', 'invoices', 'subscriptions', 'payments']
    },
    { 
      id: 'admin-reports', 
      label: 'Reports', 
      description: 'Analytics and insights',
      icon: BarChart3, 
      path: '/admin/reports?tab=analytics', 
      category: 'admin',
      keywords: ['analytics', 'reports', 'insights', 'data']
    },
    { 
      id: 'admin-support', 
      label: 'Support', 
      description: 'Manage support tickets',
      icon: LifeBuoy, 
      path: '/admin/support', 
      category: 'admin',
      keywords: ['support', 'tickets', 'help']
    },
    { 
      id: 'admin-settings', 
      label: 'Settings', 
      description: 'Email & integrations',
      icon: Settings, 
      path: '/admin/settings', 
      category: 'admin',
      keywords: ['settings', 'integrations', 'mailchimp', 'email']
    },
  ].filter(item => {
    if (item.id === 'admin-billing' && !canManageBilling) return false;
    if (item.id === 'admin-reports' && !canViewReports) return false;
    return true;
  });

  // Sysco Hub items (if user has access)
  const syscoItems = canManageSysco ? [
    { 
      id: 'sysco-health', 
      label: 'Sysco Health', 
      description: 'System health monitor',
      icon: Activity, 
      path: '/admin/sysco?tab=health', 
      category: 'admin',
      keywords: ['sysco', 'health', 'status', 'integration']
    },
    { 
      id: 'sysco-exports', 
      label: 'Sysco Exports', 
      description: 'Export logs',
      icon: Truck, 
      path: '/admin/sysco?tab=exports', 
      category: 'admin',
      keywords: ['sysco', 'exports', 'orders', 'logs']
    },
    { 
      id: 'sysco-inbound', 
      label: 'Sysco Inbound', 
      description: 'Inbound file processing',
      icon: FileText, 
      path: '/admin/sysco?tab=inbound', 
      category: 'admin',
      keywords: ['sysco', 'inbound', 'files', 'processing']
    },
    { 
      id: 'sysco-confirmations', 
      label: 'Sysco Confirmations', 
      description: 'Order confirmations',
      icon: FileCheck, 
      path: '/admin/sysco?tab=confirmations', 
      category: 'admin',
      keywords: ['sysco', 'confirmations', 'orders']
    },
    { 
      id: 'sysco-invoices', 
      label: 'Sysco Invoices', 
      description: 'Invoice management',
      icon: Receipt, 
      path: '/admin/sysco?tab=invoices', 
      category: 'admin',
      keywords: ['sysco', 'invoices', 'billing']
    },
    { 
      id: 'sysco-settings', 
      label: 'Sysco Settings', 
      description: 'Configure Sysco integration',
      icon: Settings, 
      path: '/admin/sysco?tab=settings', 
      category: 'admin',
      keywords: ['sysco', 'settings', 'configuration']
    },
  ] : [];

  // Enterprise items (super admin only)
  const enterpriseItems = isSuperAdmin ? [
    { 
      id: 'enterprise-overview', 
      label: 'Enterprise Overview', 
      description: 'Global dashboard',
      icon: LayoutDashboard, 
      path: '/admin/enterprise/overview', 
      category: 'enterprise',
      keywords: ['enterprise', 'overview', 'dashboard']
    },
    { 
      id: 'enterprise-organizations', 
      label: 'Organizations', 
      description: 'Manage all organizations',
      icon: Building2, 
      path: '/admin/enterprise/organizations', 
      category: 'enterprise',
      keywords: ['enterprise', 'organizations', 'orgs']
    },
    { 
      id: 'enterprise-users', 
      label: 'All Users', 
      description: 'Global user management',
      icon: Users2, 
      path: '/admin/enterprise/users', 
      category: 'enterprise',
      keywords: ['enterprise', 'users', 'global']
    },
    { 
      id: 'enterprise-provisioning', 
      label: 'Provisioning', 
      description: 'User provisioning queue',
      icon: UserPlus, 
      path: '/admin/enterprise/provisioning', 
      category: 'enterprise',
      keywords: ['enterprise', 'provisioning', 'queue']
    },
    { 
      id: 'enterprise-security', 
      label: 'Security', 
      description: 'Security monitoring',
      icon: ShieldCheck, 
      path: '/admin/enterprise/security', 
      category: 'enterprise',
      keywords: ['enterprise', 'security', 'monitoring']
    },
    { 
      id: 'enterprise-settings', 
      label: 'System Settings', 
      description: 'Global system settings',
      icon: Settings2, 
      path: '/admin/enterprise/settings', 
      category: 'enterprise',
      keywords: ['enterprise', 'settings', 'system']
    },
  ] : [];

  const allItems = [...adminItems, ...syscoItems, ...enterpriseItems];

  // Get recent items
  const recentItems = recentPages
    .map(path => allItems.find(item => item.path === path))
    .filter(Boolean) as CommandItem[];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search admin features..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        {recentItems.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentItems.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.description} ${item.keywords?.join(' ')}`}
                    onSelect={() => handleSelect(item.path)}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span>{item.label}</span>
                      {item.description && (
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Admin">
          {adminItems.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.id}
                value={`${item.label} ${item.description} ${item.keywords?.join(' ')}`}
                onSelect={() => handleSelect(item.path)}
              >
                <Icon className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>{item.label}</span>
                  {item.description && (
                    <span className="text-xs text-muted-foreground">{item.description}</span>
                  )}
                </div>
              </CommandItem>
            );
          })}
        </CommandGroup>

        {syscoItems.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Sysco Hub">
              {syscoItems.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.description} ${item.keywords?.join(' ')}`}
                    onSelect={() => handleSelect(item.path)}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span>{item.label}</span>
                      {item.description && (
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      )}
                    </div>
                    <Badge variant="outline" className="ml-auto text-xs">
                      Sysco
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {enterpriseItems.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Enterprise">
              {enterpriseItems.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.description} ${item.keywords?.join(' ')}`}
                    onSelect={() => handleSelect(item.path)}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span>{item.label}</span>
                      {item.description && (
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      )}
                    </div>
                    <Badge variant="outline" className="ml-auto text-xs bg-purple-500/20 text-purple-700 border-purple-500/30">
                      Enterprise
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};
